import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  type RouterHistory,
  useRouter,
  useRouterState,
} from '@tanstack/react-router';
import { useCallback, useContext, useEffect, useRef } from 'react';
import type { z } from 'zod';
import { SessionProvider } from '@/api/session-provider';
import { de } from '@/i18n/de';
import { AppShell } from '@/shell/layout/app-shell';
import { ThemeToggle } from '@/shell/theme/theme-toggle';
import { NavigationProvider } from '@/shell/ui/internal-link';
import { SkipLink } from '@/shell/ui/visually-hidden';
import { NoAccessPage, NotFoundPage, RouteErrorPage } from '@/states/pages';
import { FocusHeading, FocusRequest, PageHost } from './page-host';
import { ENTRIES, NO_ACCESS_PATH, ROUTE_TABLE, type RouteEntry, STATES_PATH } from './route-table';
import { SessionOptionsContext } from './session-options';
import { StatesGallery } from './states-gallery';

/** Die Zustandsansicht gibt es nur in Entwicklung und im E2E-Bau. */
const STATES_ENABLED = import.meta.env.MODE !== 'production';

function titleOf(routeId: string): string {
  if (routeId === NO_ACCESS_PATH) {
    return de.pages.kein_zugang;
  }
  if (routeId === STATES_PATH) {
    return de.pages.zustaende;
  }
  const entry = ROUTE_TABLE.find((candidate) => candidate.id === routeId);
  return entry === undefined ? de.pages.nicht_gefunden : de.pages[entry.title];
}

/** Seite ohne Navigation für Konten ohne Zugang. */
function NoAccessLayout() {
  return (
    <div className="min-h-dvh bg-surface-page text-ink">
      <SkipLink />
      <header className="flex min-h-14 items-center justify-between border-b border-line bg-surface-block px-4">
        <span className="text-lead font-bold tracking-tight">{de.app.name}</span>
        <ThemeToggle />
      </header>
      <main id="inhalt" tabIndex={-1} className="mx-auto max-w-xl px-4 py-16 outline-none">
        <Outlet />
      </main>
    </div>
  );
}

function RootLayout() {
  const router = useRouter();
  const options = useContext(SessionOptionsContext);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const routeId = useRouterState({ select: (state) => state.matches.at(-1)?.routeId ?? '' });
  const resolvedPath = useRouterState({
    select: (state) => state.resolvedLocation?.pathname ?? state.location.pathname,
  });
  const noAccess = pathname === NO_ACCESS_PATH;

  // Beim ersten Laden bleibt der Fokus, wo er ist; nach jedem Seitenwechsel bekommt die Überschrift ihn.
  const focusRequest = useRef(false);
  const lastPath = useRef<string | null>(null);
  if (lastPath.current !== resolvedPath) {
    focusRequest.current = lastPath.current !== null;
    lastPath.current = resolvedPath;
  }

  const title = titleOf(routeId);
  useEffect(() => {
    document.title = `${title}${de.app.titleSuffix}`;
  }, [title]);

  const navigate = useCallback((to: string) => router.history.push(to), [router]);
  const onNoAccess = useCallback(() => router.history.replace(NO_ACCESS_PATH), [router]);

  return (
    <FocusRequest.Provider value={focusRequest}>
      <NavigationProvider navigate={navigate}>
        <SessionProvider loadMe={!noAccess} onNoAccess={onNoAccess} {...options}>
          {noAccess ? (
            <NoAccessLayout />
          ) : (
            <AppShell pathname={pathname}>
              <Outlet />
            </AppShell>
          )}
        </SessionProvider>
      </NavigationProvider>
    </FocusRequest.Provider>
  );
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: () => (
    <>
      <NotFoundPage />
      <FocusHeading />
    </>
  ),
});

/** Eine Seite aus dem URL-Vertrag: Pfad und Suchparameter aus der Tabelle, Inhalt aus der Seitendatei. */
function pageRoute<const E extends RouteEntry>(entry: E) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path: entry.path,
    validateSearch: entry.validateSearch as (raw: Record<string, unknown>) => z.output<E['search']>,
    component: () => <PageHost file={entry.file} title={entry.title} />,
  });
}

const noAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NO_ACCESS_PATH,
  component: () => (
    <>
      <NoAccessPage />
      <FocusHeading />
    </>
  ),
});

const statesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: STATES_PATH,
  component: StatesGallery,
});

const routeTree = rootRoute.addChildren([
  pageRoute(ENTRIES.heute),
  pageRoute(ENTRIES.menschen),
  pageRoute(ENTRIES.menschenDetail),
  pageRoute(ENTRIES.gespraechDetail),
  pageRoute(ENTRIES.netzwerk),
  pageRoute(ENTRIES.konto),
  pageRoute(ENTRIES.einstellungen),
  pageRoute(ENTRIES.freigaben),
  pageRoute(ENTRIES.datenschutz),
  pageRoute(ENTRIES.verbindungen),
  pageRoute(ENTRIES.freigabe),
  pageRoute(ENTRIES.zielpersonen),
  pageRoute(ENTRIES.zielpersonenDetail),
  pageRoute(ENTRIES.neueZielpersonen),
  pageRoute(ENTRIES.nachrichten),
  pageRoute(ENTRIES.nachrichtenDetail),
  pageRoute(ENTRIES.zielgruppen),
  pageRoute(ENTRIES.zielgruppenDetail),
  pageRoute(ENTRIES.ziele),
  pageRoute(ENTRIES.ablaeufe),
  pageRoute(ENTRIES.einwilligung),
  pageRoute(ENTRIES.sichtbarkeit),
  pageRoute(ENTRIES.werkstatt),
  pageRoute(ENTRIES.themen),
  pageRoute(ENTRIES.kommentieren),
  pageRoute(ENTRIES.fragen),
  pageRoute(ENTRIES.stimme),
  pageRoute(ENTRIES.regeln),
  pageRoute(ENTRIES.fakten),
  pageRoute(ENTRIES.plan),
  pageRoute(ENTRIES.wirkung),
  noAccessRoute,
  ...(STATES_ENABLED ? [statesRoute] : []),
]);

export function createAppRouter(options: { history?: RouterHistory } = {}) {
  return createRouter({
    routeTree,
    defaultErrorComponent: ({ error }) => <RouteErrorPage error={error} />,
    ...(options.history === undefined ? {} : { history: options.history }),
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
